// Email Service - Unified email account management API calls (Gmail + Outlook)

import api from './api';
import { buildQueryString } from '../utils/urlHelpers';
import type {
  EmailAccount,
  EmailAccountDetail,
  EmailAccountPurpose,
  EmailAccountStatus,
  EmailProvider,
  EmailStats,
  EmailTestResult,
  EmailAuthStartResponse,
} from '../types/api';

export const emailService = {
  /**
   * Get all email accounts (both Gmail and Outlook)
   */
  getAccounts: async (params?: {
    status?: EmailAccountStatus;
    purpose?: EmailAccountPurpose;
    provider?: EmailProvider;
  }): Promise<EmailAccount[]> => {
    const queryString = buildQueryString({
      status: params?.status,
      purpose: params?.purpose,
      provider: params?.provider,
    });

    const url = queryString ? `/email/accounts?${queryString}` : '/email/accounts';
    const response = await api.get<EmailAccount[]>(url);
    return response.data;
  },

  /**
   * Get a single email account by ID
   */
  getAccount: async (accountId: string): Promise<EmailAccountDetail> => {
    const response = await api.get<EmailAccountDetail>(`/email/accounts/${accountId}`);
    return response.data;
  },

  /**
   * Get email account stats summary
   */
  getStats: async (): Promise<EmailStats> => {
    const response = await api.get<EmailStats>('/email/stats');
    return response.data;
  },

  /**
   * Start OAuth flow for a new Gmail account
   */
  startGmailAuth: async (purpose: EmailAccountPurpose = 'general'): Promise<EmailAuthStartResponse> => {
    const response = await api.get<EmailAuthStartResponse>(
      `/email/gmail/auth/start?purpose=${purpose}`
    );
    return response.data;
  },

  /**
   * Start OAuth flow for a new Outlook account
   */
  startOutlookAuth: async (purpose: EmailAccountPurpose = 'general'): Promise<EmailAuthStartResponse> => {
    const response = await api.get<EmailAuthStartResponse>(
      `/email/outlook/auth/start?purpose=${purpose}`
    );
    return response.data;
  },

  /**
   * Enable an email account
   */
  enableAccount: async (accountId: string): Promise<EmailAccount> => {
    const response = await api.post<EmailAccount>(`/email/accounts/${accountId}/enable`);
    return response.data;
  },

  /**
   * Disable an email account
   */
  disableAccount: async (accountId: string): Promise<EmailAccount> => {
    const response = await api.post<EmailAccount>(`/email/accounts/${accountId}/disable`);
    return response.data;
  },

  /**
   * Set account as primary
   */
  setPrimary: async (accountId: string): Promise<EmailAccount> => {
    const response = await api.post<EmailAccount>(`/email/accounts/${accountId}/set-primary`);
    return response.data;
  },

  /**
   * Delete an email account
   */
  deleteAccount: async (
    accountId: string,
    revokeTokens: boolean = true
  ): Promise<{ status: string; account_id: string; tokens_revoked: boolean }> => {
    const response = await api.delete<{
      status: string;
      account_id: string;
      tokens_revoked: boolean;
    }>(`/email/accounts/${accountId}?revoke_tokens=${revokeTokens}`);
    return response.data;
  },

  /**
   * Test an email account (health check + optional send test email)
   */
  testAccount: async (
    accountId: string,
    sendEmail: boolean = false,
    recipient?: string
  ): Promise<EmailTestResult> => {
    const queryString = buildQueryString({
      send_email: sendEmail,
      recipient,
    });

    const response = await api.post<EmailTestResult>(
      `/email/accounts/${accountId}/test?${queryString}`
    );
    return response.data;
  },

  /**
   * Get health check for an email account
   */
  getHealth: async (
    accountId: string
  ): Promise<{ account_id: string; email: string; provider: string; status: string; health: Record<string, unknown> }> => {
    const response = await api.get<{
      account_id: string;
      email: string;
      provider: string;
      status: string;
      health: Record<string, unknown>;
    }>(`/email/accounts/${accountId}/health`);
    return response.data;
  },

  /**
   * Dispatch async sync for an account.
   * Returns { status: "dispatched", task_id, account_id, provider, full_sync }.
   */
  syncAccount: async (
    accountId: string,
    fullSync: boolean = false
  ): Promise<Record<string, unknown>> => {
    const response = await api.post<Record<string, unknown>>(
      `/email/accounts/${accountId}/sync?full_sync=${fullSync}`
    );
    return response.data;
  },

  /**
   * Dispatch async sync for all accounts.
   * Returns { status: "dispatched", task_id, provider }.
   */
  syncAll: async (provider?: EmailProvider): Promise<Record<string, unknown>> => {
    const params = provider ? `?provider=${provider}` : '';
    const response = await api.post<Record<string, unknown>>(`/email/sync/all${params}`);
    return response.data;
  },

  /**
   * Poll the status of an async sync task.
   * Returns { task_id, state, result?, error? }.
   */
  getSyncStatus: async (taskId: string): Promise<{
    task_id: string;
    state: string;
    result?: Record<string, unknown>;
    error?: string;
  }> => {
    const response = await api.get<{
      task_id: string;
      state: string;
      result?: Record<string, unknown>;
      error?: string;
    }>(`/email/sync/status/${taskId}`);
    return response.data;
  },
};

export default emailService;
